import { Box, Input } from "@chakra-ui/react";
import { customEvent } from "goi_common";
import { baseColors, BaseButton, BaseStack } from "goi_common/src/design-system";
import { GoiBaseBottomSheet } from "goi_common/src/components/BottomSheet/GoiBaseBottomSheet";
import { formatPhoneNumber } from "goi_common/src/utils/parsePhoneNumber";
import { useEffect, useState } from "react";
import { 장례준비긴급도_LIST } from "@/containers/MatchContainer/constants/request";
import { useSubmitLead } from "@/hooks/useSubmitLead";
import { useGetServerTime } from "@/hooks/useGetServerTime";
import {
  NIGHT_CONSENT_URL,
  OPS_GUIDE_MESSAGE,
  PRIVACY_CONSENT_URL,
  SANGJO_LANDING_EVENT_PATH,
  SangjoLandingTrigger,
  URGENCY_TO_LEAD_STATUS,
} from "./sangjoLandingData";
import { SangjoLandingFormValue } from "./SangjoLandingForm";

const PHONE_REGEX = /^010-\d{4}-\d{4}$/;

/**
 * 상조 랜딩 "정확한 견적 상담 신청" 바텀시트.
 * 전화번호 + 개인정보 고지(체크박스 없음, 제출 = 동의) → v2 리드(home) 제출.
 * 기초수급자 랜딩 바텀시트와 같은 리드 흐름(야간 긴급 수신 동의 포함).
 */
export default function SangjoLandingConsultSheet({
  isOpen,
  formValue,
  onClose,
  onSubmitted,
}: {
  isOpen: boolean;
  /** 폼에서 이미 고른 값. 미선택 항목은 빈 문자열. */
  formValue: SangjoLandingFormValue;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const { submitLead } = useSubmitLead();
  const { data: serverTime } = useGetServerTime();
  const isNight = serverTime?.is_night ?? false;

  // 폼에서 이미 고른 현재 상황은 그대로 쓰고 시트에서 다시 묻지 않는다
  const askStatus = !formValue.status;
  useEffect(() => {
    if (isOpen) setStatus(formValue.status);
  }, [isOpen, formValue.status]);

  // 임박도는 필수(무빈소 리드 모달과 같은 정책). v2 리드 status 로 매핑돼 전송된다
  const submitEnabled = !!status && PHONE_REGEX.test(phone) && !loading;

  const handleClose = () => {
    if (loading) return;
    customEvent({ newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target: "sheet_close" } });
    onClose();
  };

  const handleSubmit = async () => {
    if (!submitEnabled) return;
    customEvent({
      newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target: "sheet_submit" },
    });
    setLoading(true);
    try {
      await submitLead({
        phone,
        lead_type: "home",
        slack_message: [
          "*[상조 견적 상담 신청]*",
          "",
          OPS_GUIDE_MESSAGE,
          "",
          `희망 지역: *${formValue.regionText || "미입력"}*`,
          `예상 조문객 수: *${formValue.condolers || "미입력"}*`,
          `준비 상황: *${status}*`,
        ].join("\n"),
        status: URGENCY_TO_LEAD_STATUS[status],
        accept_night_call: isNight ? true : undefined,
      });
      customEvent({
        newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target: "sheet_success" },
      });
      onSubmitted();
    } catch (e) {
      customEvent({
        newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target: "sheet_fail" },
      });
      console.error("리드 제출 실패:", e);
      alert("잠시 오류가 발생했습니다. 문제가 지속될 시 고객센터(1666-9784)로 연락주세요.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoiBaseBottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      header={
        <BaseStack spacing={8} pr="28px">
          <Box className="headline_1_sb" color={baseColors.label_normal}>
            우리 가족에게 맞는
            <br />
            상세 견적을 안내해 드려요.
          </Box>
          <Box className="body_2_normal_r" color={baseColors.label_alternative_1}>
            장례 전문 상담원이 필요한 서비스와 예상 비용을 자세히 안내해 드립니다. 견적 확인 후
            결정하셔도 괜찮아요.
          </Box>
        </BaseStack>
      }
    >
      <BaseStack spacing={16} p="0 20px 20px">
        {askStatus && (
          <Box>
            <Box className="label_1_sb" color={baseColors.label_normal} pb="8px">
              현재 상황{" "}
              <Box as="span" color={baseColors.primary_orange}>
                *
              </Box>
            </Box>
            <BaseStack spacing={8}>
              {장례준비긴급도_LIST.map((label) => {
                const selected = status === label;
                return (
                  <Box
                    key={label}
                    as="button"
                    width="100%"
                    p="12px 16px"
                    borderRadius="6px"
                    textAlign="left"
                    cursor="pointer"
                    className={selected ? "body_2_normal_sb" : "body_2_normal_r"}
                    bg={selected ? baseColors.label_normal : baseColors.background_normal}
                    color={selected ? baseColors.white : baseColors.label_alternative_1}
                    boxShadow={selected ? "none" : `inset 0 0 0 1px ${baseColors.line_normal}`}
                    onClick={() => {
                      setStatus(label);
                      customEvent({
                        newGtm: {
                          path: SANGJO_LANDING_EVENT_PATH,
                          trigger_and_target: "sheet_status-answer",
                        },
                      });
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </BaseStack>
          </Box>
        )}

        <Box>
          <Box className="label_1_sb" color={baseColors.label_normal} pb="8px">
            연락처{" "}
            <Box as="span" color={baseColors.primary_orange}>
              *
            </Box>
          </Box>
          <Input
            type="tel"
            inputMode="numeric"
            maxLength={13}
            placeholder="010-0000-0000"
            value={phone}
            onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
            height="48px"
            className="body_1_normal_r"
            border={`1px solid ${baseColors.line_accent}`}
            borderRadius="6px"
            focusBorderColor={baseColors.primary_orange}
          />
          <Box className="caption_1_r" color={baseColors.label_assistive_1} pt="6px">
            상담을 위해 전화를 드리는 용도 외에는 사용하지 않아요.
          </Box>
        </Box>

        <ConsentRow
          label="개인정보 수집·이용 동의 (필수)"
          href={PRIVACY_CONSENT_URL}
          trigger="sheet_privacy_link"
        />
        {/* 야간에만 노출되는 긴급 수신 동의 고지(기초수급자·무빈소와 동일). 제출 = 동의 */}
        {isNight && (
          <ConsentRow
            label="긴급 상황 시 수신 동의 (야간, 필수)"
            href={NIGHT_CONSENT_URL}
            trigger="sheet_night_link"
          />
        )}

        <BaseButton type="primary" size="large" disabled={!submitEnabled} onClick={handleSubmit}>
          <Box className="body_1_normal_m">동의하고 상세 견적 안내받기</Box>
        </BaseButton>
      </BaseStack>
    </GoiBaseBottomSheet>
  );
}

const ConsentRow = ({
  label,
  href,
  trigger,
}: {
  label: string;
  href: string;
  trigger: SangjoLandingTrigger;
}) => (
  <BaseStack direction="horizontal" justifyContent="space-between" alignItems="center" spacing={8}>
    <Box className="label_1_r" color={baseColors.label_alternative_1}>
      {label}
    </Box>
    <a
      className="label_2_m"
      style={{ color: baseColors.label_assistive_1, textDecoration: "underline", flexShrink: 0 }}
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={() =>
        customEvent({ newGtm: { path: SANGJO_LANDING_EVENT_PATH, trigger_and_target: trigger } })
      }
    >
      내용 보기
    </a>
  </BaseStack>
);
